import React, { useCallback, useEffect, useRef, useState } from 'react'
import OutsideClickHandler from 'react-outside-click-handler'

import Chat from './index'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const ChatModal: React.FC<Props> = ({ isOpen, onClose }) => {
  // Modal position and size state
  const [position, setPosition] = useState({ x: 0, y: 50 })
  const [size, setSize] = useState({ width: 500, height: 600 })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  })

  const modalRef = useRef<HTMLDivElement>(null)

  // Initialize position to the right side of screen only when first opened
  const [hasInitialized, setHasInitialized] = useState(false)

  useEffect(() => {
    if (isOpen && !hasInitialized) {
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight

      setPosition({
        x: windowWidth - size.width - 20, // 20px margin from right edge
        y: Math.max(200, windowHeight * 0.25), // Position at 30% down from top of screen
      })
      setHasInitialized(true)
    } else if (!isOpen) {
      // Reset initialization flag when modal closes
      setHasInitialized(false)
    }
  }, [isOpen, hasInitialized, size.width, size.height])

  // Handle drag start
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (
        e.target === e.currentTarget ||
        (e.target as HTMLElement).closest('.drag-handle')
      ) {
        setIsDragging(true)
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        })
        e.preventDefault()
      }
    },
    [position]
  )

  // Handle resize start
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true)
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
      })
      e.preventDefault()
      e.stopPropagation()
    },
    [size]
  )

  // Handle mouse move for drag and resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x
        const newY = e.clientY - dragStart.y

        // Keep modal within screen bounds
        const maxX = window.innerWidth - size.width
        const maxY = window.innerHeight - size.height

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        })
      }

      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x
        const deltaY = e.clientY - resizeStart.y

        const newWidth = Math.max(300, resizeStart.width + deltaX)
        const newHeight = Math.max(400, resizeStart.height + deltaY)

        // Keep within screen bounds based on current position (not changing position during resize)
        const maxWidth = window.innerWidth - position.x
        const maxHeight = window.innerHeight - position.y

        setSize({
          width: Math.min(newWidth, maxWidth),
          height: Math.min(newHeight, maxHeight),
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setIsResizing(false)
    }

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }

    // Always return a cleanup function to satisfy TypeScript
    return () => {}
  }, [isDragging, isResizing, dragStart, resizeStart, position, size])

  // Prevent text selection during drag/resize
  useEffect(() => {
    if (isDragging || isResizing) {
      document.body.style.userSelect = 'none'
      document.body.style.cursor = isDragging ? 'move' : 'se-resize'
    } else {
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }

    return () => {
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
  }, [isDragging, isResizing])

  if (!isOpen) return null

  return (
    <>
      {/* Temporary: Icon preview (hidden for now) */}
      {/* <IconPreview /> */}

      <div className="fixed inset-0 z-50 bg-black bg-opacity-30">
        <OutsideClickHandler onOutsideClick={onClose}>
          <div
            ref={modalRef}
            className="absolute rounded-lg bg-white shadow-2xl"
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              width: `${size.width}px`,
              height: `${size.height}px`,
            }}
          >
            {/* Modal Header - Draggable */}
            <div
              className="drag-handle flex cursor-move select-none items-center justify-between border-b border-gray-200 px-6 py-4"
              onMouseDown={handleMouseDown}
            >
              <h2 className="pointer-events-none text-lg font-semibold text-gray-800">
                Assistente de IA
              </h2>
              <button
                onClick={onClose}
                className="pointer-events-auto rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-info-dark"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="h-[calc(100%-64px)] overflow-hidden">
              <Chat className="h-full" />
            </div>

            {/* Resize Handle */}
            <div
              className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize opacity-50 hover:opacity-100"
              onMouseDown={handleResizeMouseDown}
            >
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                {/* AI Sparkle/Magic icon */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.091 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
            </div>
          </div>
        </OutsideClickHandler>
      </div>
    </>
  )
}

export default ChatModal
